import { useState } from 'react';
import type { DailyContentResult, DailyPostItem } from '../types';
import { SOCIAL_PLATFORMS } from '../types';
import SocialGenerationProgress from './SocialGenerationProgress';

interface DailyContentResultsProps {
  result: DailyContentResult | null;
  isGenerating: boolean;
  platform: string;
  quantity: string;
  onRegenerate: () => void;
  onRegenerateDay?: (day: number) => void;
}

const CATEGORY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  Engagement: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' },
  Educational: { bg: 'bg-teal-100', text: 'text-teal-700', border: 'border-teal-200' },
  Story: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
  'Trust Building': { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200' },
  'Soft Promo': { bg: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-200' },
  Motivational: { bg: 'bg-violet-100', text: 'text-violet-700', border: 'border-violet-200' },
  'Behind the Scenes': { bg: 'bg-stone-100', text: 'text-stone-700', border: 'border-stone-200' },
};

function getCategoryStyle(category: string) {
  return CATEGORY_COLORS[category] ?? { bg: 'bg-stone-100', text: 'text-stone-600', border: 'border-stone-200' };
}

interface EditableField {
  hook: string;
  caption: string;
  cta: string;
  hashtags: string;
  imageDirection: string;
  shortVideoIdea: string;
}

function DayCard({
  post,
  onRegenerate,
}: {
  post: DailyPostItem;
  onRegenerate?: () => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [edited, setEdited] = useState<EditableField>({
    hook: post.hook ?? '',
    caption: post.caption ?? '',
    cta: post.cta ?? '',
    hashtags: (post.hashtags ?? []).join(' '),
    imageDirection: post.imageDirection ?? '',
    shortVideoIdea: post.shortVideoIdea ?? '',
  });
  const [copiedCaption, setCopiedCaption] = useState(false);
  const [duplicated, setDuplicated] = useState(false);

  const catStyle = getCategoryStyle(post.contentCategory);

  const handleCopyCaption = () => {
    navigator.clipboard.writeText(edited.caption);
    setCopiedCaption(true);
    setTimeout(() => setCopiedCaption(false), 2000);
  };

  const handleDuplicate = () => {
    const text = `Day ${post.day} — ${post.postType}\n\nHook:\n${edited.hook}\n\nCaption:\n${edited.caption}\n\nCTA: ${edited.cta}\n\nHashtags: ${edited.hashtags}\n\nImage Direction: ${edited.imageDirection}${edited.shortVideoIdea ? `\n\nShort-form Video Idea: ${edited.shortVideoIdea}` : ''}`;
    navigator.clipboard.writeText(text);
    setDuplicated(true);
    setTimeout(() => setDuplicated(false), 2000);
  };

  return (
    <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden hover:border-orange-200 transition-all duration-200">
      {/* Card header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-stone-50 border-b border-stone-100">
        <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br from-orange-500 to-amber-400 text-white text-sm font-bold shrink-0">
          {post.day}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold text-stone-800">Day {post.day}</span>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${catStyle.bg} ${catStyle.text} ${catStyle.border}`}>
              {post.contentCategory}
            </span>
          </div>
          <p className="text-xs text-stone-400 mt-0.5 truncate">{post.postType}</p>
        </div>
        {/* Actions */}
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={handleDuplicate}
            title="Copy full post"
            className={`w-7 h-7 flex items-center justify-center rounded-lg border transition-all duration-200 cursor-pointer ${
              duplicated
                ? 'bg-green-500 border-green-500 text-white'
                : 'bg-white border-stone-200 text-stone-400 hover:border-orange-300 hover:text-orange-500'
            }`}
          >
            <i className={`${duplicated ? 'ri-check-line' : 'ri-file-copy-line'} text-xs`} />
          </button>
          <button
            onClick={() => setIsEditing((v) => !v)}
            title="Edit"
            className={`w-7 h-7 flex items-center justify-center rounded-lg border transition-all duration-200 cursor-pointer ${
              isEditing
                ? 'bg-orange-500 border-orange-500 text-white'
                : 'bg-white border-stone-200 text-stone-400 hover:border-orange-300 hover:text-orange-500'
            }`}
          >
            <i className={`${isEditing ? 'ri-check-line' : 'ri-edit-line'} text-xs`} />
          </button>
          {onRegenerate && (
            <button
              onClick={onRegenerate}
              title="Regenerate this day"
              className="w-7 h-7 flex items-center justify-center rounded-lg border border-stone-200 bg-white text-stone-400 hover:border-orange-300 hover:text-orange-500 transition-all duration-200 cursor-pointer"
            >
              <i className="ri-refresh-line text-xs" />
            </button>
          )}
        </div>
      </div>

      {/* Card body */}
      <div className="px-4 py-4 space-y-3">
        {edited.hook && (
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-5 h-5 flex items-center justify-center rounded-md bg-amber-100 shrink-0">
                <i className="ri-flashlight-fill text-amber-600 text-xs" />
              </div>
              <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Hook</span>
            </div>
            {isEditing ? (
              <input
                type="text"
                value={edited.hook}
                onChange={(e) => setEdited((prev) => ({ ...prev, hook: e.target.value }))}
                className="w-full text-xs px-3 py-2 rounded-xl border border-orange-300 bg-orange-50/30 text-stone-800 focus:outline-none focus:border-orange-400 transition-all duration-200"
              />
            ) : (
              <p className="text-sm font-semibold text-stone-700 leading-relaxed">{edited.hook}</p>
            )}
          </div>
        )}

        {/* Caption */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Caption</span>
            <button
              onClick={handleCopyCaption}
              className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                copiedCaption
                  ? 'bg-green-500 border-green-500 text-white'
                  : 'bg-stone-50 border-stone-200 text-stone-500 hover:border-orange-300 hover:text-orange-500'
              }`}
            >
              <i className={`${copiedCaption ? 'ri-check-line' : 'ri-file-copy-line'} text-xs`} />
              {copiedCaption ? 'Copied!' : 'Copy'}
            </button>
          </div>
          {isEditing ? (
            <textarea
              value={edited.caption}
              onChange={(e) => setEdited((prev) => ({ ...prev, caption: e.target.value }))}
              rows={4}
              maxLength={500}
              className="w-full text-sm px-3 py-2.5 rounded-xl border border-orange-300 bg-orange-50/30 text-stone-800 focus:outline-none focus:border-orange-400 resize-none transition-all duration-200"
            />
          ) : (
            <p className="text-sm text-stone-700 leading-relaxed whitespace-pre-wrap">{edited.caption}</p>
          )}
        </div>

        {/* CTA */}
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center rounded-md bg-teal-100 shrink-0">
            <i className="ri-cursor-line text-teal-600 text-xs" />
          </div>
          <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider shrink-0">CTA</span>
          {isEditing ? (
            <input
              type="text"
              value={edited.cta}
              onChange={(e) => setEdited((prev) => ({ ...prev, cta: e.target.value }))}
              className="flex-1 text-xs px-2.5 py-1.5 rounded-lg border border-orange-300 bg-orange-50/30 text-stone-800 focus:outline-none focus:border-orange-400 transition-all duration-200"
            />
          ) : (
            <span className="text-xs font-semibold text-teal-700 bg-teal-50 border border-teal-200 px-2.5 py-1 rounded-full whitespace-nowrap">
              {edited.cta}
            </span>
          )}
        </div>

        {edited.hashtags && (
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-5 h-5 flex items-center justify-center rounded-md bg-stone-100 shrink-0">
                <i className="ri-hashtag text-stone-500 text-xs" />
              </div>
              <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Hashtags</span>
            </div>
            {isEditing ? (
              <input
                type="text"
                value={edited.hashtags}
                onChange={(e) => setEdited((prev) => ({ ...prev, hashtags: e.target.value }))}
                className="w-full text-xs px-3 py-2 rounded-xl border border-orange-300 bg-orange-50/30 text-stone-800 focus:outline-none focus:border-orange-400 transition-all duration-200"
              />
            ) : (
              <p className="text-xs text-stone-500 leading-relaxed bg-stone-50 rounded-xl px-3 py-2 border border-stone-100">
                {edited.hashtags}
              </p>
            )}
          </div>
        )}

        {/* Image Direction */}
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-5 h-5 flex items-center justify-center rounded-md bg-stone-100 shrink-0">
              <i className="ri-image-line text-stone-500 text-xs" />
            </div>
            <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Image Direction</span>
          </div>
          {isEditing ? (
            <input
              type="text"
              value={edited.imageDirection}
              onChange={(e) => setEdited((prev) => ({ ...prev, imageDirection: e.target.value }))}
              className="w-full text-xs px-3 py-2 rounded-xl border border-orange-300 bg-orange-50/30 text-stone-800 focus:outline-none focus:border-orange-400 transition-all duration-200"
            />
          ) : (
            <p className="text-xs text-stone-500 italic bg-stone-50 rounded-xl px-3 py-2 border border-stone-100 leading-relaxed">
              {edited.imageDirection}
            </p>
          )}
        </div>

        {edited.shortVideoIdea && (
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-5 h-5 flex items-center justify-center rounded-md bg-rose-100 shrink-0">
                <i className="ri-video-line text-rose-600 text-xs" />
              </div>
              <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Short-form Video Idea</span>
            </div>
            {isEditing ? (
              <input
                type="text"
                value={edited.shortVideoIdea}
                onChange={(e) => setEdited((prev) => ({ ...prev, shortVideoIdea: e.target.value }))}
                className="w-full text-xs px-3 py-2 rounded-xl border border-orange-300 bg-orange-50/30 text-stone-800 focus:outline-none focus:border-orange-400 transition-all duration-200"
              />
            ) : (
              <p className="text-xs text-stone-600 leading-relaxed bg-rose-50 rounded-xl px-3 py-2 border border-rose-100">
                {edited.shortVideoIdea}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function DailyContentResults({
  result,
  isGenerating,
  platform,
  quantity,
  onRegenerate,
  onRegenerateDay,
}: DailyContentResultsProps) {
  const [copiedAll, setCopiedAll] = useState(false);

  const platformInfo = result ? SOCIAL_PLATFORMS.find((p) => p.id === result.platform) : null;

  const handleCopyAll = () => {
    if (!result) return;
    const text = result.posts
      .map(
        (p) =>
          `=== DAY ${p.day}: ${p.postType} ===\n\nHook:\n${p.hook}\n\nCaption:\n${p.caption}\n\nCTA: ${p.cta}\n\nHashtags: ${(p.hashtags ?? []).join(' ')}\n\nImage Direction: ${p.imageDirection}${p.shortVideoIdea ? `\n\nShort-form Video Idea: ${p.shortVideoIdea}` : ''}`
      )
      .join('\n\n---\n\n');
    navigator.clipboard.writeText(text);
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2500);
  };

  /* Loading */
  if (isGenerating) {
    return <SocialGenerationProgress mode="daily" platform={platform} quantity={quantity} />;
  }

  if (isGenerating && !isGenerating) {
    return (
      <div className="bg-white rounded-2xl border border-stone-100 h-full min-h-[500px] flex flex-col items-center justify-center gap-5">
        <div className="relative">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-400">
            <i className="ri-calendar-2-line text-white text-2xl" />
          </div>
          <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-white border-2 border-white flex items-center justify-center">
            <span className="w-3 h-3 rounded-full bg-emerald-400 animate-ping" />
          </div>
        </div>
        <div className="text-center">
          <p className="text-base font-bold text-stone-900 mb-1">Building your content plan...</p>
          <p className="text-sm text-stone-400">
            Crafting varied, engaging posts for your audience
          </p>
        </div>
        <div className="flex gap-1.5 mt-1">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="w-2 h-2 rounded-full bg-emerald-400 animate-bounce"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
        <p className="text-xs text-stone-400 max-w-xs text-center px-4">
          This usually takes 20–40 seconds for a full plan
        </p>
      </div>
    );
  }

  /* Empty */
  if (!result) {
    return (
      <div className="bg-white rounded-2xl border border-stone-100 h-full min-h-[500px] flex flex-col items-center justify-center gap-5 px-8 text-center">
        <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-emerald-50 border border-emerald-100">
          <i className="ri-calendar-2-line text-emerald-400 text-3xl" />
        </div>
        <div>
          <p className="text-lg font-bold text-stone-800 mb-2">Your daily content will appear here</p>
          <p className="text-sm text-stone-400 max-w-xs leading-relaxed">
            Fill in your details and click <strong className="text-stone-600">Generate</strong> to create your content plan.
          </p>
        </div>
        <div className="flex flex-col gap-2 w-full max-w-xs">
          {[
            'Varied post types & angles',
            'Platform-optimized captions',
            'Image direction for each post',
            'CTA suggestions per post',
            '7-day full content calendar',
          ].map((item) => (
            <div key={item} className="flex items-center gap-2.5 text-sm text-stone-400">
              <i className="ri-check-line text-emerald-400 shrink-0" />
              {item}
            </div>
          ))}
        </div>
      </div>
    );
  }

  const is7Day = result.quantity === '7';
  const is3Post = result.quantity === '3';

  return (
    <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="px-4 md:px-6 py-3 md:py-4 border-b border-stone-100 flex items-center justify-between shrink-0 gap-2">
        <div className="min-w-0 shrink">
          <div className="flex items-center gap-2">
            <h2 className="text-sm md:text-base font-bold text-stone-900">
              {is7Day ? '7-Day Content Plan' : is3Post ? '3 Post Variations' : 'Daily Post'}
            </h2>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 whitespace-nowrap">
              Daily Content
            </span>
          </div>
          <p className="text-xs text-stone-400 mt-0.5 truncate flex items-center gap-1">
            <i className={`${platformInfo?.icon ?? 'ri-global-line'} text-xs`} />
            {result.platform} · <strong className="text-stone-600">{result.businessName}</strong>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleCopyAll}
            className={`flex items-center gap-1.5 text-xs font-semibold px-2.5 py-2 rounded-lg border transition-all duration-200 cursor-pointer whitespace-nowrap ${
              copiedAll
                ? 'bg-green-500 border-green-500 text-white'
                : 'bg-stone-50 border-stone-200 text-stone-600 hover:border-stone-300'
            }`}
          >
            <i className={`${copiedAll ? 'ri-check-line' : 'ri-file-copy-line'} text-xs`} />
            <span className="hidden sm:inline">{copiedAll ? 'Copied!' : 'Copy All'}</span>
          </button>
          <button
            onClick={onRegenerate}
            className="flex items-center gap-1.5 text-xs font-bold bg-gradient-to-r from-orange-500 to-amber-400 text-white px-2.5 py-2 rounded-lg hover:from-orange-600 hover:to-amber-500 transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line text-xs" />
            <span className="hidden sm:inline">Regenerate All</span>
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="overflow-y-auto flex-1">
        <div className="px-4 md:px-6 py-4 md:py-6 space-y-4">
          {/* Summary strip for 7-day */}
          {is7Day && (
            <div className="flex items-center gap-2 flex-wrap">
              {result.posts.map((p) => {
                const catStyle = getCategoryStyle(p.contentCategory);
                return (
                  <div
                    key={p.day}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${catStyle.bg} ${catStyle.text} ${catStyle.border}`}
                  >
                    <span className="font-bold">D{p.day}</span>
                    <span className="font-normal opacity-80">{p.contentCategory}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Post cards */}
          <div className="space-y-4">
            {result.posts.map((post) => (
              <DayCard
                key={post.day}
                post={post}
                onRegenerate={onRegenerateDay ? () => onRegenerateDay(post.day) : undefined}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
